import { Alert, BoxProps, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormState,
} from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';

import { updateInfo } from '@/api/user';

import { LoadingButton } from '@/components/Form/LoadingButton';
import { Select } from '@/components/Form/Select';
import { Icon } from '@/components/Icon/Icon';
import { Divider } from '@/components/Layout/Divider';
import { ConfirmDrawer } from '@/components/Layout/Drawer/ConfirmDrawer';
import { Form } from '@/components/Layout/Layout.styled';

import { ERROR_CODE } from '@/constants/api';
import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';
import { useUpdateUser } from '@/hooks/useUpdateUser';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import {
  selectAdobeSignFormFeature,
  selectConfigsState,
  selectMailingAddressFeature,
} from '@/redux/configs';

import { CommonAPIError } from '@/types/common';
import { AddressForm as AddressFormType } from '@/types/form';
import { CountryISO } from '@/types/places';
import { ResidenceType } from '@/types/user';

import {
  buildAddressFormPayload,
  getPermanentAddressFromUser,
  getTenantDefaultValues,
} from '@/utils/address';
import { getAPIDataError, getAPIErrorMessage } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import { isGuyanaTenant } from '@/utils/tenant';
import {
  getNormalizedTaxResidences,
  getOtherTins,
  isTrinidadOrTobagoUser,
} from '@/utils/user';

import { getSSNTINConfig } from '../../utils/tin';
import AddressFields from './AddressFields';
import { CountryOfResidenceField } from './CountryOfResidenceField';
import { MailingAddressSection } from './MailingAddressSection';
import { SSNTINFields } from './SSNTINFields';
import { TaxResidencySection } from './TaxResidencySection';

type AddressFormProps = {
  onFinished: () => void;
} & BoxProps;

export function AddressForm({ onFinished, ...props }: AddressFormProps) {
  const updateUser = useUpdateUser();
  const user = useAppSelector(selectLoggedInUser);
  const isTTUser = isTrinidadOrTobagoUser(user);
  const { country: translatedCountry, t } = useTranslate();

  const {
    requiredLocalTIN,
    isoCode: localISO,
    residenceOptions,
  } = useAppSelector(selectConfigsState);
  const shouldAddMailingAddress = useAppSelector(selectMailingAddressFeature);
  const isAdobeSignFormEnabled = useAppSelector(selectAdobeSignFormFeature);
  const isEnhancedTaxSectionVisible = !isAdobeSignFormEnabled;

  const otherTins = useMemo(
    () => getOtherTins(user?.tin, [CountryISO.US, localISO]),
    [user?.tin, localISO],
  );

  const isGuyanaUser = useMemo(
    () => isGuyanaTenant(user?.tenant),
    [user?.tenant],
  );

  const { value: isPending, setValue: setPending } = useBoolean(false);

  const translatedResidenceOptions = useMemo(
    () =>
      (residenceOptions || [])
        .filter(residenceType => residenceType.value)
        .map(residenceType => ({
          value: residenceType.value,
          text: residenceType.i18nKey ? t(residenceType.i18nKey) : '',
        })),
    [residenceOptions, t],
  );

  const normalizedTaxResidences = useMemo(
    () => getNormalizedTaxResidences(user?.taxResidences),
    [user?.taxResidences],
  );
  const permanentAddressSource = useMemo(
    () => getPermanentAddressFromUser(user),
    [user],
  );

  const isDifferentTaxResidency = useMemo(() => {
    if (normalizedTaxResidences.length > 1) {
      return true;
    }

    if (normalizedTaxResidences.length === 1) {
      return normalizedTaxResidences[0] !== permanentAddressSource?.country;
    }

    return false;
  }, [normalizedTaxResidences, permanentAddressSource?.country]);
  const userAddress = useMemo<AddressFormType>(() => {
    const defaultCountry = isTTUser ? user?.citizenship || '' : localISO;
    return {
      line1: permanentAddressSource?.line1 ?? '',
      city: permanentAddressSource?.city ?? '',
      ...(user ? getTenantDefaultValues(user.tenant, user) : {}),
      zip: permanentAddressSource?.zip ?? '',
      country: permanentAddressSource?.country || defaultCountry,
      residence: permanentAddressSource?.residence,
      taxResidences: normalizedTaxResidences || [],
      differentTaxResidency: isDifferentTaxResidency,
      usTin: user?.tin?.[CountryISO.US] || '',
      localTin: requiredLocalTIN ? user?.tin?.[localISO] || '' : '',
      otherTins,
    };
  }, [
    user,
    permanentAddressSource,
    requiredLocalTIN,
    localISO,
    isTTUser,
    otherTins,
    normalizedTaxResidences,
    isDifferentTaxResidency,
  ]);

  const methods = useForm<AddressFormType>({
    defaultValues: {
      line1: '',
      city: '',
      poBoxDistrict: '',
      zip: '',
      state: '',
      country: isTTUser ? '' : localISO,
      taxResidences: [],
      differentTaxResidency: false,
      usTin: '',
      localTin: '',
      mailingSameAsResidential: true,
      mailingLine1: '',
      mailingCity: '',
      mailingState: '',
      mailingZip: '',
      mailingCountry: '',
      residenceCountry: '',
      residence: ResidenceType.Empty,
    },
    values: userAddress,
    resetOptions: {
      keepDirtyValues: true,
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { errors: visibleErrors },
  } = methods;

  const { mutate, error, reset } = useMutation({
    mutationFn: updateInfo,
    onSuccess: () => {
      setPending(false);
      onFinished();
    },
    onError: async (e: AxiosError<CommonAPIError>) => {
      if (
        e.response?.data.error === ERROR_CODE.AGE_UNDER_18 ||
        e.response?.data.error === ERROR_CODE.ADDRESS_NOT_CAYMAN
      ) {
        try {
          await updateUser();
        } catch (e) {
          console.error(e);
        }
      }
      setPending(false);
    },
  });

  const apiErrorCode = getAPIDataError<{ error?: string }>(error);

  const shouldShowApiError =
    apiErrorCode !== ERROR_CODE.AGE_UNDER_18 &&
    apiErrorCode !== ERROR_CODE.ADDRESS_NOT_CAYMAN;

  const {
    value: isConfirmingSubmit,
    setTrue: showConfirm,
    setFalse: hideConfirm,
  } = useBoolean();

  const onSubmit = handleSubmit(rawPayload => {
    hideConfirm();
    setPending(true);
    if (user) {
      const { taxResidences, country, differentTaxResidency } = rawPayload;
      const { shouldShowUsTin, shouldShowLocalTin, otherCountriesTin } =
        getSSNTINConfig({
          country,
          differentTaxResidency,
          localISO,
          requiredLocalTIN,
          taxResidences: taxResidences || [],
          user,
          isGuyanaUser,
        });
      const payload = buildAddressFormPayload(rawPayload, user.tenant, {
        shouldShowUsTin,
        shouldShowLocalTin,
        otherCountriesTin,
        localISO,
        shouldAddMailingAddress,
      });

      reset();
      mutate(payload);
    }
  });

  const popularCountryOptions = COUNTRIES_WITH_POPULAR.map(item => ({
    ...item,
    text: translatedCountry[item.value],
  }));

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(showConfirm)} {...props}>
        <AddressFields
          isPending={isPending}
          popularCountryOptions={popularCountryOptions}
          tenant={user?.tenant}
        />

        {residenceOptions && (
          <Controller
            name="residence"
            control={control}
            render={({ fieldState: { error } }) => (
              <Select
                options={translatedResidenceOptions}
                control={control}
                name="residence"
                error={!!error}
                helperText={error?.message}
                rules={{
                  required: t(
                    'createNewAccount.addressPage.form.residence.error.required',
                  ),
                }}
                fullWidth
                disabled={isPending}
                placeholder={t(
                  'createNewAccount.addressPage.form.residence.placeHolder',
                )}
                label={t('createNewAccount.addressPage.form.residence.label')}
              />
            )}
          />
        )}
        {shouldAddMailingAddress && (
          <Stack direction="column" width="100%">
            <MailingAddressSection
              isDisabled={isPending}
              popularCountryOptions={popularCountryOptions}
            />
            <Divider containerProps={{ my: 2 }} width="50%" />
            <CountryOfResidenceField
              isDisabled={isPending}
              popularCountryOptions={popularCountryOptions}
            />
          </Stack>
        )}

        {!isEnhancedTaxSectionVisible && (
          <>
            <TaxResidencySection
              control={control}
              disabled={isPending}
              popularCountryOptions={popularCountryOptions}
              taxResidencesError={!!visibleErrors.taxResidences}
              taxResidencesHelperText={visibleErrors.taxResidences?.message}
            />
            <SSNTINFields user={user} isPending={isPending} />
          </>
        )}

        {error && shouldShowApiError && (
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        )}

        <SubmitButton isPending={isPending} />
      </Form>

      {isConfirmingSubmit && (
        <ConfirmDrawer
          title={t('createNewAccount.addressPage.form.drawer.title')}
          description={t(
            'createNewAccount.addressPage.form.drawer.description',
          )}
          primaryAction={onSubmit}
          primaryText={t(
            'createNewAccount.addressPage.form.drawer.button.submit',
          )}
          secondaryAction={hideConfirm}
          secondaryText={t(
            'createNewAccount.addressPage.form.drawer.button.cancel',
          )}
          onClose={hideConfirm}
        />
      )}
    </FormProvider>
  );
}

const SubmitButton = ({ isPending }: { isPending: boolean }) => {
  const { isValid } = useFormState<AddressFormType>();
  const { t } = useTranslate();
  return (
    <LoadingButton
      {...getDataTestId('address-form-submit-button')}
      type="submit"
      fullWidth
      disabled={!isValid || isPending}
      isLoading={isPending}
    >
      {t('createNewAccount.addressPage.form.button.submit')}
    </LoadingButton>
  );
};
